<template>
  <div class="dashboard-page">
    <div v-if="isLoading" class="text-center py-3">
      <b-spinner label="Loading dashboard..." />
    </div>
    <b-alert v-else-if="loadError" show variant="danger" class="mb-2">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchDashboard">Retry</b-button>
    </b-alert>

    <template v-else>
    <b-card no-body class="mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
          <h4 class="mb-1 mb-md-0 font-weight-bolder">Last Updates</h4>
          <div class="d-flex flex-wrap align-items-center last-updates-legend">
            <small v-if="summaryPeriodLabel" class="text-muted mr-1 mb-50">{{ summaryPeriodLabel }}</small>
            <div class="legend-item">
              <span class="legend-dot legend-dot--teal" />
              Advertisers : {{ summary.advertisers }}
            </div>
            <div class="legend-item">
              <span class="legend-dot legend-dot--mint" />
              Users : {{ summary.users }}
            </div>
            <div class="legend-item mr-0">
              <span class="legend-dot legend-dot--blue" />
              Live Ads : {{ summary.live_ads }}
            </div>
          </div>
          <small class="text-muted chart-period-hint d-none d-md-block">New sign-ups in chart period</small>
        </div>

        <vue-apex-charts
          type="area"
          height="340"
          :options="chartOptions"
          :series="chartSeries"
        />
      </b-card-body>
    </b-card>

    <b-row class="match-height mb-2">
      <b-col cols="12" md="4" v-for="item in statsCards" :key="item.title">
        <b-card no-body class="stats-card">
          <b-card-body class="d-flex align-items-center justify-content-between">
            <div>
              <h5 class="mb-25 font-weight-bolder">{{ item.title }}</h5>
              <small class="text-muted d-block mb-1">This month: {{ item.thisMonth }}</small>
              <h2 class="mb-25">{{ item.total }}</h2>
              <small class="stats-trend">{{ item.trend }} vs last month</small>
            </div>
            <div class="stats-circle">
              <strong>{{ item.thisMonth }}</strong>
              <small>New</small>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col cols="12" lg="5" class="mb-2 mb-lg-0">
        <b-card no-body class="dashboard-table-card">
          <b-card-body class="pb-0 pt-1 flex-grow-0">
            <div class="d-flex align-items-start justify-content-between mb-1">
              <div>
                <h5 class="mb-25 font-weight-bolder">Recent Ads</h5>
                <small class="text-muted">{{ pendingAdsTotal }} pending · showing {{ recentAds.length }}</small>
              </div>
              <small class="text-uppercase table-head-label">Actions</small>
            </div>
          </b-card-body>
          <div v-if="!recentAds.length" class="text-center text-muted py-2 px-1">
            No recent ads.
          </div>
          <div v-else class="recent-ads-list">
            <div class="recent-ad-row" v-for="item in recentAds" :key="`ad-${item.id}`">
              <div class="d-flex align-items-center">
                <b-avatar size="34" :src="item.avatar" />
                <div class="ml-1">
                  <div class="recent-name">{{ item.name }}</div>
                  <small class="text-muted">{{ item.date }}</small>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <span class="status-pill mr-1" :class="item.statusClass">{{ item.statusLabel }}</span>
                <div v-if="item.isPending" class="action-icons">
                  <feather-icon
                    icon="XIcon"
                    size="16"
                    class="action-icon-btn"
                    :class="{ 'action-icon-btn--disabled': isProcessing(`ad-reject-${item.id}`) }"
                    @click="rejectAd(item)"
                  />
                  <feather-icon
                    icon="CheckIcon"
                    size="16"
                    class="ml-75 action-icon-btn"
                    :class="{ 'action-icon-btn--disabled': isProcessing(`ad-approve-${item.id}`) }"
                    @click="approveAd(item)"
                  />
                  
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" lg="7">
        <b-card no-body class="h-100 dashboard-table-card">
          <b-card-body class="pb-0 pt-1 advertisers-card-head flex-grow-0">
            <div class="advertisers-head-row">
              <h5 class="mb-0 font-weight-bolder advertisers-title">Advertisers</h5>
              <b-form-input v-model="advertiserSearch" placeholder="Search" class="search-input" />
            </div>
          </b-card-body>
          <div class="table-wrap">
            <table class="custom-table">
              <thead>
                <tr>
                  <th class="checkbox-col">
                    <input type="checkbox" />
                  </th>
                  <th class="sortable-th" @click="toggleAdvertiserSort('name')">
                    NAME
                    <span class="sort-icons" :class="{ 'is-active': advertiserSortKey === 'name' }">
                      <feather-icon icon="ChevronUpIcon" size="12" />
                      <feather-icon icon="ChevronDownIcon" size="12" />
                    </span>
                  </th>
                  <th class="text-center sortable-th" @click="toggleAdvertiserSort('actions')">
                    Actions
                    <span class="sort-icons" :class="{ 'is-active': advertiserSortKey === 'actions' }">
                      <feather-icon icon="ChevronUpIcon" size="12" />
                      <feather-icon icon="ChevronDownIcon" size="12" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredAdvertisers.length">
                  <td colspan="3" class="text-center text-muted py-2">
                    No advertisers found.
                  </td>
                </tr>
                <tr v-for="item in paginatedAdvertisers" :key="`adv-${item.id}`">
                  <td class="checkbox-col">
                    <input type="checkbox" />
                  </td>
                  <td>
                    <div class="d-flex align-items-center">
                      <b-avatar size="34" :src="item.avatar" />
                      <div class="ml-1">
                        <div class="recent-name">{{ item.name }}</div>
                        <small class="text-muted">{{ item.joined }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="text-center action-icons">
                    <template v-if="item.isPending">
                      <feather-icon
                        icon="XIcon"
                        size="16"
                        class="action-icon-btn"
                        :class="{ 'action-icon-btn--disabled': isProcessing(`adv-reject-${item.id}`) }"
                        @click="rejectAdvertiser(item)"
                      />
                      <feather-icon
                        icon="CheckIcon"
                        size="16"
                        class="ml-75 action-icon-btn"
                        :class="{ 'action-icon-btn--disabled': isProcessing(`adv-approve-${item.id}`) }"
                        @click="approveAdvertiser(item)"
                      />
                    </template>
                    <small v-else class="text-muted text-capitalize">{{ item.statusLabel }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-card-body class="pt-50 d-flex align-items-center justify-content-between">
            <small class="text-muted">{{ advertisersPaginationLabel }}</small>
            <b-pagination
              size="sm"
              :total-rows="filteredAdvertisers.length"
              :per-page="advertisersPerPage"
              v-model="currentPage"
              pills
              prev-text="Previous"
              next-text="Next"
              align="right"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormInput,
  BPagination,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

import { adminPanelApi } from '@/services/adminPanelApi'
import { formatAdminDate, resolveMediaUrl, unwrapAdminResponse, isAdPendingStatus, extractApiErrorMessage } from '@/utils/adminApiHelpers'
import { filterDemoRecords, isDemoDisplayName } from '@/utils/userDisplay'
import {
  confirmAction,
  notifyError,
  notifySuccess,
  promptRejectReason,
} from '@/utils/uiActions'

export default {
  components: {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormInput,
    BPagination,
    BRow,
    BSpinner,
    VueApexCharts,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      processingKey: null,
      summaryPeriodLabel: '',
      advertiserSearch: '',
      advertiserSortKey: 'name',
      advertiserSortDesc: false,
      summary: { advertisers: 0, users: 0, live_ads: 0, period_days: 30 },
      advertisersPerPage: 7,
      currentPage: 1,
      chartSeries: [
        { name: 'Advertisers', data: [] },
        { name: 'Users', data: [] },
        { name: 'Live Ads', data: [] },
      ],
      chartOptions: {
        chart: { toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 1.5 },
        fill: { type: 'solid', opacity: 0.65 },
        legend: { show: false },
        xaxis: {
          categories: [],
          axisBorder: { show: false },
          labels: {
            rotate: -45,
            hideOverlappingLabels: true,
          },
        },
        yaxis: { min: 0, tickAmount: 4 },
        grid: { borderColor: '#eff2f7' },
        colors: ['#1ecab8', '#8adfb3', '#8ecae6'],
        tooltip: { shared: true },
      },
      statsCards: [
        { title: 'Total Users', total: 0, thisMonth: 0, trend: '^ 0%' },
        { title: 'Total Company', total: 0, thisMonth: 0, trend: '^ 0%' },
        { title: 'Active Ads', total: 0, thisMonth: 0, trend: '^ 0%' },
      ],
      pendingAdsTotal: 0,
      recentAds: [],
      advertisers: [],
    }
  },
  computed: {
    filteredAdvertisers() {
      const term = (this.advertiserSearch || '').trim().toLowerCase()
      let list = this.advertisers.filter(a => !isDemoDisplayName(a.name))
      if (term) {
        list = list.filter(a => (a.name || '').toLowerCase().includes(term))
      }
      const key = this.advertiserSortKey
      const dir = this.advertiserSortDesc ? -1 : 1
      return [...list].sort((a, b) => {
        if (key === 'actions') {
          const av = a.isPending ? 0 : 1
          const bv = b.isPending ? 0 : 1
          return (av - bv) * dir
        }
        return String(a[key] || '').localeCompare(String(b[key] || ''), undefined, { sensitivity: 'base' }) * dir
      })
    },
    paginatedAdvertisers() {
      const start = (this.currentPage - 1) * this.advertisersPerPage
      return this.filteredAdvertisers.slice(start, start + this.advertisersPerPage)
    },
    advertisersPaginationLabel() {
      const total = this.filteredAdvertisers.length
      if (!total) return 'No advertisers'
      const start = (this.currentPage - 1) * this.advertisersPerPage + 1
      const end = Math.min(this.currentPage * this.advertisersPerPage, total)
      return `Showing ${start} to ${end} of ${total} entries`
    },
  },
  watch: {
    advertiserSearch() {
      this.currentPage = 1
    },
  },
  created() {
    this.fetchDashboard()
  },
  methods: {
    toggleAdvertiserSort(key) {
      if (this.advertiserSortKey === key) {
        this.advertiserSortDesc = !this.advertiserSortDesc
      } else {
        this.advertiserSortKey = key
        this.advertiserSortDesc = false
      }
    },
    isProcessing(key) {
      return this.processingKey === key
    },
    formatTrend(growth) {
      const n = Number(growth)
      if (Number.isNaN(n)) return '^ 0%'
      return `${n >= 0 ? '^' : 'v'} ${Math.abs(n).toFixed(1)}%`
    },
    normalizeStatus(status) {
      return (status || 'pending').toLowerCase()
    },
    statusClass(status) {
      const s = this.normalizeStatus(status)
      if (s.includes('reject')) return 'status-pill--rejected'
      if (s.includes('pend')) return 'status-pill--pending'
      if (s.includes('approv') || s.includes('active')) return 'status-pill--approved'
      return 'status-pill--pending'
    },
    statusLabel(status) {
      const s = this.normalizeStatus(status)
      if (s.includes('reject')) return 'Rejected'
      if (s.includes('pend')) return 'Pending'
      if (s.includes('approv') || s.includes('active')) return 'Approved'
      return 'Pending'
    },
    mapRecentAd(ad) {
      const status = this.normalizeStatus(ad.status)
      return {
        id: ad.id,
        name: ad.ad_title || 'Ad',
        date: formatAdminDate(ad.created_at),
        status,
        statusLabel: this.statusLabel(status),
        statusClass: this.statusClass(status),
        isPending: isAdPendingStatus(ad.status),
        avatar: resolveMediaUrl(ad.profile_image),
      }
    },
    mapAdvertiser(a) {
      const status = this.normalizeStatus(a.status)
      return {
        id: a.id,
        name: a.name || 'Advertiser',
        joined: formatAdminDate(a.joined),
        status,
        statusLabel: this.statusLabel(status),
        isPending: status === 'pending',
        avatar: resolveMediaUrl(a.avatar),
      }
    },
    async fetchDashboard() {
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.dashboard()
        const data = unwrapAdminResponse(res)
        const summary = data.summary || {}
        const totals = data.totals || {}
        const kpis = data.kpis || {}
        const analytics = data.analytics || []
        const periodDays = summary.period_days ?? 30

        this.summary = {
          advertisers: summary.advertisers ?? 0,
          users: summary.users ?? 0,
          live_ads: summary.live_ads ?? 0,
          period_days: periodDays,
        }
        this.summaryPeriodLabel = `Last ${periodDays} days`
        this.pendingAdsTotal = totals.pending_ads ?? 0

        const card = (title, kpi, totalFallback) => {
          const total = kpi?.total ?? kpi?.count ?? totalFallback ?? 0
          const thisMonth = kpi?.this_month ?? kpi?.monthly_count ?? 0
          return {
            title,
            total,
            thisMonth,
            trend: this.formatTrend(kpi?.growth),
          }
        }

        this.statsCards = [
          card('Total Users', kpis.total_users, totals.users),
          card('Total Company', kpis.total_company, totals.advertisers),
          card('Active Ads', kpis.active_ads, totals.live_ads),
        ]

        if (analytics.length) {
          const maxVal = Math.max(
            1,
            ...analytics.flatMap(a => [a.advertisers ?? 0, a.users ?? 0, a.ads ?? 0]),
          )
          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              ...this.chartOptions.xaxis,
              categories: analytics.map(a => a.date),
            },
            yaxis: { min: 0, max: maxVal, tickAmount: 4 },
          }
          this.chartSeries = [
            { name: 'Advertisers', data: analytics.map(a => a.advertisers ?? 0) },
            { name: 'Users', data: analytics.map(a => a.users ?? 0) },
            { name: 'Live Ads', data: analytics.map(a => a.ads ?? 0) },
          ]
        } else {
          this.chartSeries = [
            { name: 'Advertisers', data: [] },
            { name: 'Users', data: [] },
            { name: 'Live Ads', data: [] },
          ]
        }

        this.recentAds = filterDemoRecords(
          (data.recent_ads || []).map(ad => this.mapRecentAd(ad)),
          'name',
        )
        this.advertisers = filterDemoRecords(
          (data.recent_advertisers || []).map(a => this.mapAdvertiser(a)),
          'name',
        )
        this.currentPage = 1
      } catch (e) {
        const status = e.response?.status
        if (status === 401 || status === 403) {
          this.loadError = e.response?.data?.message || 'You do not have permission to view the dashboard.'
        } else {
          this.loadError = e.response?.data?.message || 'Unable to load dashboard.'
        }
        this.recentAds = []
        this.advertisers = []
      } finally {
        this.isLoading = false
      }
    },
    approveAd(item) {
      if (!item?.id || this.processingKey) return
      confirmAction(
        this,
        { title: 'Approve ad?', text: `Approve "${item.name}"?`, confirmButtonText: 'Approve' },
        async () => {
          const key = `ad-approve-${item.id}`
          this.processingKey = key
          try {
            await adminPanelApi.approveAd(item.id)
            notifySuccess(this, 'Ad approved successfully.')
            await this.fetchDashboard()
          } catch (e) {
            notifyError(this, extractApiErrorMessage(e, 'Unable to approve ad.'))
          } finally {
            this.processingKey = null
          }
        },
      )
    },
    async rejectAd(item) {
      if (!item?.id || this.processingKey) return
      const reason = await promptRejectReason(this, {
        title: 'Reject ad',
        text: `Provide a reason for rejecting "${item.name}".`,
      })
      if (!reason) return

      const key = `ad-reject-${item.id}`
      this.processingKey = key
      try {
        await adminPanelApi.rejectAd({ id: item.id, reason })
        notifySuccess(this, 'Ad rejected successfully.')
        await this.fetchDashboard()
      } catch (e) {
        notifyError(this, extractApiErrorMessage(e, 'Unable to reject ad.'))
      } finally {
        this.processingKey = null
      }
    },
    approveAdvertiser(item) {
      if (!item?.id || this.processingKey) return
      confirmAction(
        this,
        {
          title: 'Approve advertiser?',
          text: `Approve "${item.name}"?`,
          confirmButtonText: 'Approve',
        },
        async () => {
          const key = `adv-approve-${item.id}`
          this.processingKey = key
          try {
            await adminPanelApi.approveAdvertiser(item.id)
            notifySuccess(this, 'Advertiser approved successfully.')
            await this.fetchDashboard()
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to approve advertiser.')
          } finally {
            this.processingKey = null
          }
        },
      )
    },
    async rejectAdvertiser(item) {
      if (!item?.id || this.processingKey) return
      const reason = await promptRejectReason(this, {
        title: 'Reject advertiser',
        text: `Provide a reason for rejecting "${item.name}".`,
      })
      if (!reason) return

      const key = `adv-reject-${item.id}`
      this.processingKey = key
      try {
        await adminPanelApi.rejectAdvertiser({ id: item.id, reason })
        notifySuccess(this, 'Advertiser rejected successfully.')
        await this.fetchDashboard()
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to reject advertiser.')
      } finally {
        this.processingKey = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.date-input {
  width: 140px;
}

.last-updates-legend {
  gap: 1.2rem;
}

.legend-item {
  color: #6e6b7b;
  font-size: 0.95rem;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 0.35rem;
}

.legend-dot--teal {
  background: #1ecab8;
}

.legend-dot--mint {
  background: #8adfb3;
}

.legend-dot--blue {
  background: #8ecae6;
}

.stats-card {
  min-height: 132px;
}

.stats-circle {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 7px solid #2ac769;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #2f2b3d;
}

.stats-circle small {
  font-size: 0.7rem;
  color: #6e6b7b;
}

.stats-trend {
  color: #28c76f;
  font-weight: 600;
}

.table-wrap {
  overflow-x: auto;
}

.dashboard-table-card {
  border: 1px solid #ebe9f1;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
}

.custom-table th,
.custom-table td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #ebe9f1;
  vertical-align: middle;
  white-space: nowrap;
}

.custom-table th {
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #6e6b7b;
  letter-spacing: 0.04rem;
}

.table-head-label {
  color: #6e6b7b;
  letter-spacing: 0.04rem;
  font-size: 0.7rem;
}

.recent-ads-list {
  padding: 0 1rem 0.75rem;
  margin-top: 0;
}

.recent-ad-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #ebe9f1;
  padding: 0.72rem 0;
  gap: 0.75rem;
}

.recent-ad-row:first-child {
  border-top: none;
  padding-top: 0.2rem;
}

.recent-name {
  font-size: 0.88rem;
  color: #2f2b3d;
  line-height: 1.1;
}

.status-pill {
  border-radius: 6px;
  padding: 0.2rem 0.5rem;
  font-size: 0.78rem;
  font-weight: 600;
}

.advertisers-card-head {
  padding: 1.25rem 1.5rem 1rem !important;
  border-bottom: 1px solid #ebe9f1;
}

.advertisers-card-head + .table-wrap .custom-table thead th {
  border-top: none;
}

.advertisers-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.advertisers-title {
  flex-shrink: 0;
  color: #5d596d;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.2;
}

.advertisers-card-head .search-input {
  width: 220px;
  margin-bottom: 0;
  flex-shrink: 0;

  ::v-deep .form-control {
    height: 38px;
    padding: 0.45rem 0.85rem;
    border-radius: 6px;
    border-color: #dbdade;
    background-color: #fff;
    font-size: 0.9rem;
    color: #5d596d;
    box-shadow: none;

    &::placeholder {
      color: #a5a3ae;
      opacity: 1;
    }

    &:focus {
      border-color: #dbdade;
      box-shadow: none;
    }
  }
}

.sortable-th {
  cursor: pointer;
  user-select: none;
}

.sort-icons {
  display: inline-flex;
  flex-direction: column;
  margin-left: 0.25rem;
  vertical-align: middle;
  line-height: 0.65;
  color: #b9b9c3;
}

.sort-icons.is-active {
  color: #041122;
}

.status-pill--rejected {
  background: #ffe2e3;
  color: #ea5455;
}

.status-pill--pending {
  background: #fff5d8;
  color: #ff9f43;
}

.status-pill--approved {
  background: #dff9f3;
  color: #00cfe8;
}

.action-icons {
  color: #6e6b7b;
  white-space: nowrap;
}

.action-icon-btn {
  cursor: pointer;
}

.action-icon-btn--disabled {
  opacity: 0.4;
  pointer-events: none;
}

.checkbox-col {
  width: 36px;
}

.checkbox-col input {
  width: 13px;
  height: 13px;
}

::v-deep .pagination {
  margin-bottom: 0;
}

::v-deep .page-item .page-link {
  padding: 0.32rem 0.58rem;
  font-size: 0.78rem;
}

@media (max-width: 767px) {
  .date-input,
  .advertisers-card-head .search-input {
    width: 100%;
  }
}
</style>

<style lang="scss">
.horizontal-menu .header-navbar.navbar-horizontal ul#main-menu-navigation > li.active > a {
  background: #0b1f39 !important;
  color: #fff !important;
  border-radius: 4px;
}

.horizontal-menu .header-navbar.navbar-horizontal ul#main-menu-navigation > li > a {
  color: #6e6b7b;
}
</style>
